import React from 'react'
import { Snackbar, Alert, Button } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { createClickAwayListenerProps } from './toastUtils'
import { MonthlySummaryNotificationData } from './types'

const useStyles = makeStyles((theme) => {
  const baseFontStyles = {
    fontFamily: '"IBM Plex Sans"',
    fontStyle: 'normal',
  }

  const baseButtonStyles = {
    ...baseFontStyles,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'center' as const,
    fontWeight: 600,
    lineHeight: '26px',
    letterSpacing: '0.3px',
    textTransform: 'uppercase' as const,
  }

  return {
    summaryToastContent: {
      ...baseFontStyles,
      padding: theme.spacing(3),
      color: theme.palette.text.primary,
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 'normal',
      letterSpacing: '-0.002px',
    },
    buttonContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'flex-end',
    },
    snoozeButton: {
      ...baseButtonStyles,
      backgroundColor: 'transparent',
      color: theme.palette.error.main,
      border: 'none',
      borderRadius: theme.spacing(0.5),
      '&:hover': {
        backgroundColor: `${theme.palette.error.main}0A`, // 4% opacity
      },
    },
    startButton: {
      ...baseButtonStyles,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      border: `1px solid ${theme.palette.error.main}`,
      borderRadius: theme.spacing(1),
      boxShadow: '0 2px 6px 0 rgba(109, 182, 108, 0.16)',
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
        borderColor: theme.palette.error.dark,
      },
    },
  }
})

interface MonthlySummaryToastProps {
  appearance?: 'error' | 'info' | 'success' | 'warning'
  autoDismissTimeout?: number
  children?: React.ReactNode
  onDismiss: (id?: string) => void
  data?: MonthlySummaryNotificationData
  onSnooze?: () => void
  onCreateMonthlySummaryNote?: () => void
}

export const MonthlySummaryToast: React.FC<MonthlySummaryToastProps> = (props) => {
  const {
    appearance = 'error',
    autoDismissTimeout,
    children,
    onDismiss,
    data,
    onSnooze,
    onCreateMonthlySummaryNote,
  } = props
  const classes = useStyles()

  if (data) {
    const { firstname, lastname } = data
    const patientName = firstname && lastname ? `${firstname} ${lastname}` : 'Patient'

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        autoHideDuration={autoDismissTimeout}
        onClose={autoDismissTimeout && autoDismissTimeout > 0 ? () => onDismiss() : undefined}
        open
        ClickAwayListenerProps={createClickAwayListenerProps(autoDismissTimeout, onDismiss)}
      >
        <Alert severity={appearance} elevation={6} icon={false} onClose={undefined} sx={{ width: '480px' }}>
          <div>
            <div className={classes.summaryToastContent}>
              It&apos;s time to submit {patientName}&apos;s monthly summary note.
            </div>
            <div className={classes.buttonContainer}>
              <Button
                onClick={() => {
                  onSnooze?.()
                  onDismiss()
                }}
                className={classes.snoozeButton}
                variant="text"
              >
                snooze
              </Button>
              <Button
                onClick={() => {
                  onCreateMonthlySummaryNote?.()
                  onDismiss()
                }}
                className={classes.startButton}
                variant="contained"
              >
                start now
              </Button>
            </div>
          </div>
        </Alert>
      </Snackbar>
    )
  }

  // Fallback to default toast behavior
  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      autoHideDuration={autoDismissTimeout}
      onClose={autoDismissTimeout && autoDismissTimeout > 0 ? () => onDismiss() : undefined}
      open
      ClickAwayListenerProps={createClickAwayListenerProps(autoDismissTimeout, onDismiss)}
    >
      <Alert elevation={6}>{children}</Alert>
    </Snackbar>
  )
}
